import React, { useEffect, useMemo, useRef } from 'react';
import { Box, ThemeProvider as MuiThemeProvider } from '@mui/material';
import { useRecoilValue } from 'recoil';
import { tenantAtom } from './state/tenantAtom';
import { buildFeatureTheme } from './theme.create';
import { clearCSSVariables, injectCSSVariables } from './cssVariables';
import useTenantHydration from './hooks/useTenantHydration';
import useSystemColorMode from './hooks/useSystemColorMode';

function TenantThemeBridge({ children }) {
  useTenantHydration();
  return children;
}

/**
 * Scoped theme for feature modules only (Intervene, License, Onboarding).
 * Does not alter global App.js legacy theme, document root, or CssBaseline body styles.
 */
export function FeatureThemeProvider({ children, sx = {} }) {
  const scopeRef = useRef(null);
  const tenant = useRecoilValue(tenantAtom);
  const systemMode = useSystemColorMode(true);
  const configuredMode = tenant?.theme_config?.mode || 'system';
  const preferredMode =
    configuredMode === 'system'
      ? systemMode
      : configuredMode === 'dark'
        ? 'dark'
        : 'light';
  const preferredDensity = tenant?.theme_config?.density || 'default';

  const activeTheme = useMemo(() => {
    const mergedConfig = {
      ...(tenant?.theme_config || {}),
      mode: configuredMode,
      density: preferredDensity,
    };
    return buildFeatureTheme(mergedConfig, systemMode);
  }, [configuredMode, preferredDensity, systemMode, tenant?.theme_config]);

  useEffect(() => {
    const scopeEl = scopeRef.current;
    if (!scopeEl) {
      return undefined;
    }

    const density = activeTheme.customTokens?.density?.current || preferredDensity;
    scopeEl.setAttribute('data-feature-theme', preferredMode);
    scopeEl.setAttribute('data-feature-density', density);

    const injectedKeys = injectCSSVariables(activeTheme.customTokens, scopeEl, {
      mode: preferredMode,
      density,
    });

    return () => {
      clearCSSVariables(scopeEl, injectedKeys);
      scopeEl.removeAttribute('data-feature-theme');
      scopeEl.removeAttribute('data-feature-density');
    };
  }, [activeTheme.customTokens, preferredDensity, preferredMode]);

  return (
    <MuiThemeProvider theme={activeTheme}>
      <TenantThemeBridge>
        <Box
          ref={scopeRef}
          className="feature-theme-scope"
          sx={{
            width: '100%',
            height: '100%',
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            ...sx,
            '& > .MuiBox-root:first-child': {
                p: 0,
              },
          }}
        >
          {children}
        </Box>
      </TenantThemeBridge>
    </MuiThemeProvider>
  );
}

export default FeatureThemeProvider;
